import { useState } from 'react'
import { Link } from 'react-router'
import { Server } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Doing } from '@/components/Doing'
import { ExpertLine } from '@/components/Expert'
import { IommuWarning } from '@/components/IommuWarning'
import { PoolBarRows } from '@/components/PoolBar'
import { StatusBadge } from '@/components/StatusBadge'
import { halted, ICON_TONE, stage, unreportedWord } from '@/domain/node'
import { DASH, exact } from '@/ui/format'
import type { NodePlacement } from '../model'
import { NodeRemoveBadge } from '../NodeRemoveBadge'

// 클러스터 카드 본문의 노드 카드. 머리 줄에 아이콘(상태 색 — 정상 초록 · 준비 중 파랑 · 고장 빨강, `ICON_TONE`) · 굵은 이름 · IOMMU 경고 · 관리 IP 가 붙어 선다(IP 는 작은 글자라 이름과 밑선을 맞춘다).
// 그 줄은 줄바꿈한다: 줄어드는 것이 이름뿐이라 준비 중인 노드처럼 국면 글자와 배지가 둘 다 서면 좁은 칸에서 넘친다 — 그때 상태 묶음이 둘째 줄 오른쪽 끝으로 내려간다.
// **상태 묶음 자신도 줄바꿈한다** — 지금 하는 일 문장에 경과까지 붙어 묶음이 좁은 칸(176px)보다 넓다. 묶음을 줄지 않게 두면 둘째 줄에서도 카드 밖으로 넘친다.
// 그 아래 자원마다 한 줄씩 막대 넷(`PoolBarRows`) — 이름 · 4px 막대 · `할당/총량 단위`.
// 상태 배지는 정상(`사용 가능`)이 아닐 때만 머리 줄의 오른쪽 끝에 선다(준비 중이거나 걸음에서 멈췄으면 **지금 하는 일**이 그 왼쪽에 한 줄로 서고 — `GPU 연결 7/12` — 멈춘 것이면 위험 색이다. 대분류는 카드에 없다: 중분류 낱말이 스스로 설명한다) — 보여야 하는 것은 고장이지 정상이 아니다. VM 하나하나도 대수도 여기 없다(할당량이 VM 의 전부다).
// 오프라인 노드의 배지는 `노드 제거` 메뉴의 손잡이(`NodeRemoveBadge`)이고, 오프라인이면 IP 는 `—` 다 — 같은 주소에 다른 장비가 들어올 수 있다.
// 카드 전체가 노드 상세로 가는 링크다. 배지 자리의 클릭은 링크로 흐르지 않는다(대화 상자 안의 클릭도 React 트리로는 이 안에 있다).

export function PlacedNodeCard({ placed }: { placed: NodePlacement }) {
  const { row } = placed.node
  const [removed, setRemoved] = useState(false)
  // 제거된 노드는 목록이 다시 읽히면 빠진다. 그 사이에도 카드는 남지 않는다.
  if (removed) return null
  // 닿지 않는 설치 노드(#489)와 보고가 끊긴 노드(#503)는 그 주소가 곧 막힌 곳이라 지우지 않는다.
  const ip = row.status === 'offline' && row.node.progress?.state !== 'unreachable' && !unreportedWord(row.node) ? DASH : row.node.mgmt_ip
  // 걸음에서 멈춘 노드는 배지가 오프라인이라 국면 낱말이 사라진다 — 그 낱말이 바로
  // 어디서 멈췄는지이므로 이때도 내고, 위험 색으로 낸다. 조치 문장은 상세의 것이다.
  const stopped = halted(row.node.progress)
  return (
    <Link to={`/nodes/${row.node.id}`} className="block rounded-xl focus-visible:outline-2 focus-visible:outline-brand">
    <Card className="gap-0 border bg-card p-0 transition-colors hover:bg-surface-2">
      <div className="px-3 py-2.5">
        <div className="flex flex-wrap items-end gap-x-2 gap-y-1">
          <Server className={`mb-0.5 size-4 flex-none ${ICON_TONE[row.status]}`} />
          <span className="min-w-0 truncate leading-5 font-semibold">{row.name}</span>
          <IommuWarning failedChecks={row.node.failed_checks} />
          {ip ? <span className="flex-none text-[11px] leading-4 text-muted-foreground">{ip}</span> : null}
          {row.status === 'ready' ? null : (
            <span
              className="ml-auto flex max-w-full min-w-0 flex-wrap items-center justify-end gap-2 self-start"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
            >
              {row.status === 'preparing' || stopped ? (
                <span className={`min-w-0 text-[12px] ${stopped ? 'text-danger-ink' : 'text-muted-foreground'}`}>
                  {row.node.progress ? <Doing progress={row.node.progress} /> : stage(row.node)}
                </span>
              ) : null}
              {row.status === 'offline' ? <NodeRemoveBadge node={row.node} name={row.name} onRemoved={() => setRemoved(true)} /> : <StatusBadge status={row.status} />}
            </span>
          )}
        </div>
        <ExpertLine items={[row.node.pve_node_name, !row.node.online && row.node.last_seen_at ? `마지막 확인 ${exact(row.node.last_seen_at)}` : null]} />
        <div className="mt-2">
          <PoolBarRows cpu={placed.node.cpu} memory={placed.node.memory} storage={placed.node.storage} gpu={placed.node.gpu} />
        </div>
      </div>
    </Card>
    </Link>
  )
}
